<template>
    <div>
        <div class="uk-grid uk-grid-small">
            <div class="uk-width-2-10 lam-label-column">
                <div class="uk-form-controls-text uk-text-right">
                    {{ received_requested }}
                </div>
            </div>
            <div :class="{'uk-width-4-10': !is_contract, 'uk-width-8-10': is_contract}">
                <form-date-input
                    id="requested_at"
                    v-model="record.requested_at"
                    name="requested_at"
                    :max-date="0"
                    :editable="is_editable"
                    :required="false"
                    :error="has_error('requested_at')"
                    @input="save_field"
                />
            </div>
            <div v-if="!is_contract" class="uk-width-1-10 lam-label-column">
                <div :class="{'uk-text-danger': is_overdue}" class="uk-form-controls-text uk-text-right">
                    Due
                </div>
            </div>
            <div v-if="!is_contract" class="uk-width-3-10">
                <form-date-input
                    id="due_at"
                    v-model="record.due_at"
                    name="due_at"
                    :editable="is_editable"
                    :required="false"
                    :error="has_error('due_at')"
                    @input="save_field"
                />
            </div>

            <div v-if="!!record.sent_at" :class="{'uk-width-1-1': !record.accepted_at && !record.rejected_at, 'uk-width-6-10': !!record.accepted_at || !!record.rejected_at}">
                <div :class="{'uk-text-right': !!record.accepted_at || !!record.rejected_at, 'uk-text-center': !record.accepted_at && !record.rejected_at}" class="uk-text-small uk-text-muted">
                    <sent-info-control
                        :record="record"
                        :revision-entity="revisionEntity"
                        :editable="is_editable||is_db_admin_active"
                        class="uk-margin-small-top"
                    />
                </div>
            </div>

            <div v-if="!!record.accepted_at || !!record.rejected_at" class="uk-width-4-10">
                <div class="uk-text-small uk-text-muted uk-text-left">
                    <div v-if="!!record.accepted_at" class="uk-text-success lam-text-with-editables">
                        <div v-if="is_editable" class="uk-form-controls-text">
                            Accepted
                            <form-date-input
                                id="accepted_at"
                                v-model="record.accepted_at"
                                name="accepted_at"
                                :max-date="0"
                                :editable="true"
                                :required="false"
                                :error="has_error('accepted_at')"
                                @input="save_field"
                                class="uk-display-inline-block"
                            />
                        </div>
                        <span v-else>Accepted on {{ record.accepted_at | nibnut.date }}</span>
                    </div>
                    <div v-else class="uk-text-danger">
                        <div v-if="is_editable" class="uk-form-controls-text">
                            Rejected
                            <form-date-input
                                id="rejected_at"
                                v-model="record.rejected_at"
                                name="rejected_at"
                                :max-date="0"
                                :editable="true"
                                :required="false"
                                :error="has_error('rejected_at')"
                                @input="save_field"
                                class="uk-display-inline-block"
                            />
                        </div>
                        <span v-else>Rejected on {{ record.rejected_at | nibnut.date }}</span>
                    </div>
                </div>
            </div>
        </div>

        <div class="uk-grid uk-grid-small uk-margin-bottom">
            <div v-if="!is_nda" class="uk-width-2-10 lam-label-column">
                <div class="uk-form-controls-text uk-text-right">{{ type_label }}</div>
            </div>
            <div v-if="!is_nda" class="uk-width-4-10">
                <form-select
                    v-if="is_proposal"
                    :id="type_field"
                    :name="type_field"
                    :value="record[type_field]"
                    :options="proposal_fee_types"
                    id-field="name"
                    :editable="is_editable"
                    :required="false"
                    @input="save_field"
                />
                <form-select-combo
                    v-else
                    :id="type_field"
                    :name="type_field"
                    v-model="record[type_field]"
                    empty-label="(None)"
                    :editable="is_editable"
                    :required="false"
                    @input="save_field"
                />
            </div>
            <div v-if="!is_contract" :class="{'uk-width-1-5': is_nda, 'uk-width-1-10': !is_nda}" class="lam-label-column">
                <div class="uk-form-controls-text uk-text-right">{{ fee_label }}</div>
            </div>
            <div v-if="!is_contract" :class="{'uk-width-4-5': is_nda, 'uk-width-3-10': !is_nda}">
                <form-input
                    id="fee"
                    v-model="record.fee"
                    name="fee"
                    type="float"
                    :error="has_error('fee')"
                    :required="false"
                    :editable="((!is_proposal && !is_fee_estimate) || !record.scopes) && is_editable"
                    glyph="dollar"
                    width="1-1"
                    @input="save_field"
                    class="uk-width-1-1"
                >
                    <template v-slot:read_only>
                        <div class="lam-label-column">${{ record.fee | nibnut.number("0,0.00") }}</div>
                    </template>
                </form-input>
                <div v-if="is_editable && can_be_scoped" class="uk-text-small">
                    <base-button
                        size="mini"
                        :block="true"
                        width="1-1"
                        class="uk-margin-small-top"
                        @click.prevent="break_into_scopes"
                    >
                        Break into scopes
                    </base-button>
                </div>
            </div>
            <div v-else-if="!is_nda" class="lam-label-column uk-form-controls-text uk-width-4-10">
                <form-checkbox
                    id="prime_agreement"
                    v-model="record.prime_agreement"
                    name="prime_agreement"
                    :editable="is_editable"
                    :error="has_error('prime_agreement')"
                    @input="save_field"
                >
                    <small>Prime Agreement Provided</small>
                </form-checkbox>
            </div>

            <div v-if="is_contract" class="uk-width-2-10 lam-label-column uk-margin-top">
                <div class="uk-form-controls-text uk-text-right">
                    Decision
                </div>
            </div>
            <div v-if="is_contract" class="uk-width-3-10 uk-margin-top">
                <form-select
                    id="decision"
                    name="decision"
                    :value="record.decision"
                    :options="agreement_decisions"
                    :editable="is_editable"
                    :empty-value="null"
                    empty-label="(Pick one)"
                    :required="true"
                    @input="save_field"
                />
            </div>
            <div v-if="is_contract" class="uk-width-2-10 lam-label-column uk-margin-top">
                <div class="uk-form-controls-text uk-text-right">
                    By
                </div>
            </div>
            <div v-if="is_contract" class="uk-width-3-10 uk-margin-top">
                <form-select-user
                    id="treated_by"
                    name="treated_by"
                    :value="record.treated_by"
                    :editable="is_editable"
                    :required="false"
                    @input="save_field"
                />
            </div>

            <!-- is_service_request: status, descrition, phase/tier/... //-->
            <div v-if="is_service_request" class="uk-width-2-10 lam-label-column uk-margin-top">
                <div class="uk-form-controls-text uk-text-right">Status</div>
            </div>
            <div v-if="is_service_request" class="uk-width-8-10 uk-margin-top">
                <form-select
                    id="request_status"
                    name="request_status"
                    :value="record.request_status"
                    :options="service_status"
                    :editable="is_editable"
                    :required="true"
                    @input="save_field"
                />
            </div>
            <!--
            <div
                v-if="(!is_proposal || !record.scopes)"
                class="uk-width-2-10 lam-label-column uk-margin-top"
            >
                <div class="uk-form-controls-text uk-text-right">Description</div>
            </div>
            <div
                v-if="(!is_proposal || !record.scopes)"
                class="uk-width-8-10 uk-margin-top"
            >
                <form-input
                    :id="notes_field"
                    v-model="record[notes_field]"
                    :name="notes_field"
                    :error="has_error(notes_field)"
                    :required="false"
                    :editable="is_editable"
                    width="1-1"
                    @input="save_field"
                    class="uk-width-1-1"
                />
            </div>
            //-->
            <div v-if="is_service_request" class="uk-width-2-10 lam-label-column uk-margin-top">
                <div class="uk-form-controls-text uk-text-right">Phase</div>
            </div>
            <div v-if="is_service_request" class="uk-width-8-10 uk-margin-top">
                <select
                    v-if="is_editable"
                    v-model="card_phase"
                    class="uk-width-1-1"
                >
                    <option value="---">(Set Manually)</option>
                    <option disabled>--- Add To Existing Phase ---</option>
                    <option
                        v-for="option in phases"
                        :key="option.id"
                        :value="{request_type: 'task', phase_id: option.id}"
                    >
                        Add to "{{ option.name }}"
                    </option>
                    <option disabled>--- Create New Phase ---</option>
                    <option
                        v-for="option in project_phases"
                        :key="option.id"
                        :value="{request_type: 'phase', phase: option.id}"
                    >
                        New "{{ option.name }}" Phase
                    </option>
                </select>
                <form-input
                    v-else
                    id="card_phase_display_value"
                    :value="card_phase_display_value"
                    name="card_phase_display_value"
                    :error="has_error('card_phase_display_value')"
                    :disabled="true"
                    :editable="false"
                    :required="false"
                    width="1-1"
                    class="uk-width-1-1"
                />
            </div>
            <div
                v-if="((!is_proposal && !is_fee_estimate) || !record.scopes) && (is_editable || !!has_attachment)"
                class="uk-width-1-1 uk-margin-top"
            >
                <attachment
                    :entity="entity"
                    :owner-type="owner_type"
                    :owner-id="record.id"
                    :editable="editable"
                    size="small"
                />
            </div>
            <div v-if="!is_service_request" class="uk-width-1-5 lam-label-column uk-margin-top">
                <div class="uk-form-controls-text uk-text-right">
                    Notes
                </div>
            </div>
            <div v-if="!is_service_request" class="uk-width-4-5 uk-margin-top">
                <form-text-area
                    :id="notes_field"
                    :name="notes_field"
                    v-model="record[notes_field]"
                    width="1-1"
                    size="small"
                    :editable="is_editable"
                    :required="false"
                    @input="save_field"
                />
            </div>

            <div v-if="is_proposal" class="uk-width-2-10 lam-label-column uk-margin-top">
                <div class="uk-form-controls-text uk-text-right">Signed</div>
            </div>
            <div v-if="is_proposal" class="uk-width-8-10 uk-margin-top">
                <form-select-user
                    id="prepared_by"
                    name="prepared_by"
                    :value="record.prepared_by"
                    :editable="is_editable"
                    :required="false"
                    @input="save_field"
                />
            </div>
        </div>

        <div v-if="((!!is_proposal || !!is_fee_estimate) && !!record.scopes)">
            <h6>
                Scopes
                <base-button
                    v-if="scopes_are_editable && !project.activated_at"
                    size="mini"
                    flavor="link"
                    width="1-1"
                    @click.prevent="add_scope(0)"
                    class="uk-margin-small-left"
                >
                    <open-icon glyph="plus" />
                </base-button>
            </h6>
            <div class="lam-proposal-scopes-list">
                <div
                    v-for="(scope, index) in record.scopes"
                    :key="index"
                >
                    <div class="uk-flex uk-flex-center">
                        <span>{{ `${index * 10}`.padStart(2, '0') }}</span>
                        <form-input
                            :id="`scope-name-${index}-${record.id}`"
                            v-model="scope.name"
                            name="name"
                            :required="false"
                            :editable="scopes_are_editable"
                            :placeholder="`Scope ${('' + (index * 10)).padStart(2, '0')}`"
                            class="lam-proposal-scope-name"
                            @input="save_scopes(scope)"
                        />
                        <form-input
                            :id="`scope-total-${index}-${record.id}`"
                            v-model="scope.fee"
                            name="fee"
                            type="float"
                            :required="false"
                            :editable="scopes_are_editable"
                            align="right"
                            glyph="dollar"
                            class="lam-proposal-scope-fee"
                            @input="save_scopes(scope)"
                        />
                        <base-button
                            v-if="scopes_are_editable && !project.activated_at"
                            size="small"
                            color="danger"
                            width="1-1"
                            @click.prevent="delete_scope(index)"
                            class="uk-margin-small-right"
                        >
                            <open-icon glyph="trash" />
                        </base-button>
                    </div>
                    <div
                        v-if="!project.activated_at"
                        class="uk-margin-small-top lam-proposal-scope-subline"
                    >
                        <form-input
                            :id="`scope-description-${index}-${record.id}`"
                            v-model="scope.description"
                            name="description"
                            :required="false"
                            :editable="scopes_are_editable"
                            placeholder="Description"
                            class="lam-proposal-scope-description"
                            @input="save_scopes(scope)"
                        />
                    </div>
                    <div
                        class="uk-margin-small-top lam-proposal-scope-subline"
                    >
                        <attachment
                            :entity="entity"
                            :owner-type="owner_type"
                            :owner-id="record.id"
                            :record-id="scope.attachment_id"
                            :editable="scopes_are_editable"
                            size="small"
                            @attached="save_scope_attachment(scope, $event)"
                            @detached="save_scope_attachment(scope)"
                        />
                    </div>
                </div>
            </div>
        </div>

        <div v-if="!!is_proposal" class="uk-margin-top">
            <h6>
                Subcontractors
                <base-button
                    v-if="is_editable && !project.activated_at"
                    size="mini"
                    flavor="link"
                    width="1-1"
                    @click.prevent="add_subcontractor"
                    class="uk-margin-small-left"
                >
                    <open-icon glyph="plus" />
                </base-button>
                <span v-if="!!subcontractors.length" class="uk-float-right">
                    <strong>Total Subcontractor Fees</strong> $&nbsp;{{ subcontrator_total_fee | nibnut.number("0,0") }}
                </span>
            </h6>
            <div class="lam-proposal-subcontractors-list">
                <div
                    v-for="subcontractor in subcontractors"
                    :key="subcontractor.id"
                >
                    <div class="uk-flex uk-flex-center">
                        <firm-picker
                            :id="`subcontractor-${subcontractor.id}-firm`"
                            :value="subcontractor.firm_id"
                            :editable="is_editable"
                            @pick="save_subcontractor(subcontractor, ...arguments)"
                        />
                        <form-input
                            :id="`subcontractor-${subcontractor.id}-fee`"
                            v-model="subcontractor.fee"
                            name="fee"
                            type="float"
                            :required="false"
                            :editable="is_editable"
                            align="right"
                            glyph="dollar"
                            @input="save_subcontractor(subcontractor, ...arguments)"
                        />
                        <base-button
                            v-if="is_editable"
                            size="small"
                            color="danger"
                            width="1-1"
                            @click.prevent="delete_subcontractor(subcontractor)"
                            class="uk-margin-small-right"
                        >
                            <open-icon glyph="trash" />
                        </base-button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex"

import request_for_material_body from "./request_for_material_body"
import profile_utilities from "../../../mixins/ProfileUtilities"
import phase_utilities from "../../../mixins/PhaseUtilities"
import string_utilities from "../../../mixins/StringUtilities"

import FormDateInput from "../../../ui/FormDateInput"
import FormSelect from "../../../ui/FormSelect"
import FormSelectUser from "../../../ui/FormSelectUser"
import FormSelectCombo from "../../../ui/FormSelectCombo"
import FormInput from "../../../ui/FormInput"
import FormCheckbox from "../../../ui/FormCheckbox"
import FormTextArea from "../../../ui/FormTextArea"
import Attachment from "../../../ui/Attachment"
import BaseButton from "../../../ui/BaseButton"
import OpenIcon from "../../../ui/OpenIcon"
import SentInfoControl from "./SentInfoControl"
import FirmPicker from "../../../ui/FirmPicker"

export default {
    mixins: [request_for_material_body, string_utilities, profile_utilities, phase_utilities],
    components: {
        FormDateInput,
        FormSelect,
        FormSelectUser,
        FormSelectCombo,
        FormInput,
        FormCheckbox,
        FormTextArea,
        Attachment,
        BaseButton,
        OpenIcon,
        SentInfoControl,
        FirmPicker
    },
    methods: {
        break_into_scopes () {
            if(!!this.record && !this.record.scopes && !this.project.activated_at) this.add_scope(this.record.fee, this.attachment)
        },
        add_scope (fee = 0, attachment = null) {
            if(!!this.record && !this.project.activated_at) {
                const scopes = this.record.scopes || []
                scopes.push({ name: "", fee, description: "", attachment_id: attachment ? attachment.id : -1 })
                this.save_field_for_record_id(this.revisionEntity, this.record.id, scopes, "scopes")
            }
        },
        save_scope_attachment (scope, response = null) {
            let attachment_id = -1
            if(!!response && response.meta && response.meta.updates && response.meta.updates.attachment && !!response.meta.updates.attachment.length) {
                attachment_id = response.meta.updates.attachment[0].id
            }
            scope.attachment_id = attachment_id
            this.save_field_for_record_id(this.revisionEntity, this.record.id, this.record.scopes, "scopes")
        },
        save_scopes (scope = null) {
            if(scope) scope.fee = parseFloat(scope.fee)
            this.save_field_for_record_id(this.revisionEntity, this.record.id, this.record.scopes, "scopes")
        },
        save_subcontractor (subcontractor, value, field) {
            if(!field) return
            if(field === "firm_id") value = value.id
            this.save_field_for_record_id("firm_proposal_revision", subcontractor.id, value, field).then(() => {
                this.$store.dispatch("FETCH_RECORD", {
                    entity: "proposal",
                    id: this.record.proposal_id
                })
            })
        },
        delete_subcontractor (subcontractor) {
            this.$store.dispatch("RECORD_DELETE", {
                entity: "firm_proposal_revision",
                id: subcontractor.id
            }).then(() => {
                this.$store.dispatch("FETCH_RECORD", {
                    entity: "proposal",
                    id: this.record.proposal_id
                })
            }).catch(error => {
                this.$error(error)
            })
        },
        delete_scope (scope_index) {
            const delete_it = () => {
                const save_scopes_field = (scopes) => {
                    this.save_field_for_record_id(this.revisionEntity, this.record.id, scopes, "scopes")
                }
                let scopes = this.record.scopes.slice()
                const removed_scopes = scopes.splice(scope_index, 1)
                if(!scopes.length) {
                    scopes = null
                    save_scopes_field(scopes)
                } else {
                    scopes[0].fee += removed_scopes[0].fee
                    if(removed_scopes[0].attachment_id > 0) {
                        this.$store.dispatch("RECORD_DELETE", {
                            entity: "attachment",
                            id: removed_scopes[0].attachment_id
                        }).then(() => {
                            save_scopes_field(scopes)
                        }).catch(error => {
                            this.$error(error)
                        })
                    } else {
                        save_scopes_field(scopes)
                    }
                }
            }

            if(!this.project.activated_at) {
                const phase = this.entity_records("phase").find(phase => phase.scope_id === scope_index)
                if(phase && !!phase.scope_id) {
                    const identifier = `${scope_index}`.padStart(2, "0")
                    this.$confirm({
                        type: "error",
                        title: `Delete Scope ${identifier}`,
                        message: "Do you really want to delete this scope? All its phases will be returned to scope \"00\"",
                        cancel: `Keep Scope ${identifier}`,
                        ok: `Delete Scope ${identifier}`,
                        then: (confirmed) => {
                            if(confirmed) {
                                this.save_field_for_record_id("phase", phase.id, 0, "scope_id")
                                delete_it()
                            }
                        }
                    })
                } else delete_it()
            }
        },
        add_subcontractor () {
            this.$store.dispatch("CREATE_RECORD", {
                entity: "firm_proposal_revision",
                data: { proposal_revision_id: this.record.id }
            }).catch(error => {
                if(error) this.$error(error.message)
            })
        }
    },
    computed: {
        ...mapGetters(["entity_records", "entity_record"]),
        project () {
            return this.entity_record("project", this.parent.project_id) || {}
        },
        is_contract () {
            return this.revisionEntity === "contract_revision"
        },
        is_proposal () {
            return this.revisionEntity === "proposal_revision"
        },
        is_fee_estimate () {
            return this.revisionEntity === "fee_estimate_revision"
        },
        is_service_request () {
            return this.revisionEntity === "service_request_revision"
        },
        owner_type () {
            if(this.is_fee_estimate) return "App\\FeeEstimateRevision"
            if(this.is_proposal) return "App\\ProposalRevision"
            if(this.is_contract) return "App\\ContractRevision"
            if(this.is_service_request) return "App\\ServiceRequestRevision"
            return this.entity
        },
        is_nda () {
            if(!this.is_contract) return false
            return this.record && (this.record.agreement_type === "NDA")
        },
        is_editable () {
            if(!this.editable || !this.record) return false
            return this.is_db_admin_active || !this.record.sent_at
        },
        scopes_are_editable () {
            if(!this.editable) return false
            if(this.is_proposal) return true
            if(this.is_fee_estimate) {
                return !this.entity_records("proposal").find(record => {
                    return (record.id !== this.record.id) && (record.project_id === this.record.project_id)
                })
            }
            return false
        },
        is_overdue () {
            if(!this.record || !!this.record.sent_at || !this.record.due_at) return false
            return this.$moment(this.record.due_at).isBefore()
        },
        can_be_scoped () {
            const project = this.project
            if((this.is_proposal || this.is_fee_estimate) && !this.record.scopes && !!project && !project.activation_scope_number && this.scopes_are_editable) {
                const project_has_scoped_record = this.entity_records(this.revisionEntity).find(record => {
                    return (record.id !== this.record.id) && (record.project_id === this.record.project_id) && !!record.scopes
                })
                if(!project_has_scoped_record) {
                    return !!project.id && !project.activated_at && (project.number_suffix === "00")
                }
            }
            return false
        },
        received_requested () {
            if(this.is_contract) return "Received"
            return "Requested"
        },
        type_label () {
            if(this.is_contract) return "Type"
            return "Fee Type"
        },
        type_field () {
            if(this.is_fee_estimate || this.is_service_request) return "fee_type"
            if(this.is_proposal) return "proposal_type"
            if(this.is_contract) return "contract_type"
            return "undefined"
        },
        fee_label () {
            if(this.is_proposal) return "Fee"
            return "Amount"
        },
        notes_field () {
            if(this.is_proposal) return "description"
            return "notes"
        },
        proposal_fee_types () {
            return this.dictionary("AS_proposal_type", "name")
        },
        agreement_decisions () {
            return this.dictionary("agreement_decisions", "name")
        },
        service_levels () {
            return this.dictionary("service_level")
        },
        service_status () {
            return this.dictionary("additional_service_status")
        },
        attachment () {
            if(!this.record) return null
            return this.entity_records("attachment").find(attachment => {
                return (attachment.owner_id === this.record.id) && (attachment.owner_type === this.owner_type)
            })
        },
        has_attachment () {
            return !!this.attachment
        },
        card_phase: {
            get () {
                if(!this.record.phase && !this.record.phase_id) return "---"
                if(this.record.request_type === "task") return { request_type: this.record.request_type, phase_id: this.record.phase_id }
                return { request_type: this.record.request_type, phase: this.record.phase }
            },
            set (value) {
                let data = value
                if(!data || (data === "---")) data = { phase: null, phase_id: null }
                this.save_data_for_record_id(this.entity, this.record.id, data)
            }
        },
        phases () {
            return this.raw_phase_records.filter(phase => phase.project_id === this.parent.project_id).map(phase => {
                const phase_data = this.phase_by_id(phase.phase)
                let name = phase.title || phase_data.name
                if(phase.deltek_id) name += " (" + phase.deltek_id + ")"
                else {
                    const dates = []
                    let date = this.phase_starts_at(phase)
                    if(date) dates.push(date)
                    date = this.phase_ends_at(phase)
                    if(date) dates.push(date)
                    if(dates.length) name += " (" + dates.join(" - ") + ")"
                }
                return { id: phase.id, name: name }
            })
        },
        card_phase_display_value () {
            if(!this.record.phase && !this.record.phase_id) return "(Multiple)"
            if(this.record.request_type === "task") {
                const phase = this.phases.find(phase => phase.id === this.record.phase_id)
                if(phase) return "Add to \"" + phase.name + "\""
            } else {
                const phase = this.project_phases.find(phase => phase.id === this.record.phase)
                if(phase) return "New \"" + phase.name + "\" Phase"
            }

            return ""
        },
        subcontractors () {
            if(!this.record || !this.record.id) return []
            return this.entity_records("firm_proposal_revision").filter(firm_proposal_revision => firm_proposal_revision.proposal_revision_id === this.record.id)
        },
        subcontrator_total_fee () {
            return this.subcontractors.reduce((accumulator, subcontractor) => accumulator + subcontractor.fee, 0)
        }
    }
}
</script>

<style lang="less">
.lam-proposal-scopes-list,
.lam-proposal-subcontractors-list {
    & > div {
        margin-bottom: 1.5rem;

        & > .lam-proposal-scope-subline {
            padding-left: 2rem;
        }
        & > .uk-flex {
            & > span,
            & > button {
                flex: 0 0 auto;
            }
            & > span {
                padding: 0.2rem;
                background-color: #d8d8d8;
            }
            & > .uk-form-row {
                flex: 1 1 auto;
                margin-top: 0;
                margin-left: 0.5rem;
                margin-right: 0.5rem;

                &.lam-proposal-scope-fee {
                    flex: 0 1 150px;
                }
            }
        }

        &:last-child {
            margin-bottom: 0;
        }
    }
}
</style>
